// Given that all contents that is used to reflect the state of the database
// uses a columnar layout to preserve memory and bandwidth, here we provide a
// set of helper functions to manipulate and compute the data tables
// structures. The only assertion that we impose here would be that the table
// will always carry the `id` column, which we will be using to generate the
// loop over indices. If the `id` column does not exist, an early exist would
// be created

// Creating a copy of the table, keeping only certain column names
export function keepTableColumns(table, colnameList) {
  if (!("id" in table)) return {};

  const newTable = {};
  for (const key of Object.keys(table)) {
    if (key === "id" || colnameList.find((k) => k === key)) {
      newTable[key] = [...table[key]];
    }
  }
  return newTable;
}

// Create row object
export function createRowObj(table, idx) {
  const obj = {};
  for (const key of Object.keys(table)) {
    obj[key] = table[key][idx];
  }
  return obj;
}

// Creating a copy of the table with an extra column with a specific name and a given creation method.
// Notice that the method expects 2 arguments:
//  - The row contents as an object (the column names would be used as the object keys)
//  - The index of the table entry (optional)
export function createTableWithColumn(table, colname, method) {
  if (!("id" in table)) return {};

  const newTable = keepTableColumns(table, Object.keys(table)); // Creating a new column
  newTable[colname] = createColumn(table, method);
  return newTable;
}

export function createColumn(table, method) {
  if (!("id" in table)) return {};

  const newColumn = [];
  for (let idx = 0; idx < table.id.length; ++idx) {
    const obj = createRowObj(table, idx);
    newColumn.push(method(obj, idx));
  }
  return newColumn;
}

export function createIndexList(table) {
  if (!("id" in table)) return [];
  return [...Array.from({ length: table.id.length }, (e, i) => i)];
}

// Filtering the table such that rows that have a certain property is dropped
export function filterTable(table, method) {
  if (!("id" in table)) return {};

  const newTable = {};
  for (const key of Object.keys(table)) {
    newTable[key] = [];
  }
  for (let idx = 0; idx < table.id.length; ++idx) {
    const obj = createRowObj(table, idx);
    if (method(obj, idx)) {
      for (const key of Object.keys(table)) {
        newTable[key].push(table[key][idx]);
      }
    }
  }
  return newTable;
}

// Creating the sorting order, solution taken from stackoverflow: https://stackoverflow.com/a/65410414
let decor = (v, i) => [v, i]; // set index to value
let undecor = (a) => a[1]; // leave only index
const argsort = (arr, method) =>
  arr
    .map(decor)
    .sort((a, b) => method(a[0], b[0]))
    .map(undecor);

// Sorting the table according the sorting results of a reference array
export function sortTable(table, refArray, sortMethod) {
  if (!("id" in table)) return {};

  const order = argsort(refArray, sortMethod);
  const newTable = keepTableColumns(table, Object.keys(table));
  for (const key of Object.keys(table)) {
    newTable[key] = order.map((idx) => table[key][idx]);
  }
  return newTable;
}

// Finding the a row in the table that matches the requested result
export function findTableRow(table, matchMethod) {
  if (!("id" in table)) return null;
  for (let idx = 0; idx < table.id.length; ++idx) {
    const obj = createRowObj(table, idx);
    if (matchMethod(obj)) return obj;
  }
  return null;
}

// Faster, less generic method of finding items by skipping the row object construction
export function findByUnique(table, key, target) {
  if (!("id" in table)) return null;
  const targetIdx = table[key].indexOf(target);
  return targetIdx === -1 ? null : createRowObj(table, targetIdx);
}

// Slicing a table
export function sliceTable(table, start, end) {
  if (!("id" in table)) return {};
  const newTable = keepTableColumns(table, Object.keys(table));
  for (const key of Object.keys(table)) {
    newTable[key] = table[key].slice(start, end);
  }
  return newTable;
}

export function lenTab(table) {
  return "id" in table ? table.id.length : 0;
}

export function tabField(table, key) {
  return "id" in table ? table[key] : [];
}

// Converting the table into a list of row items. Similar to how one would call
// the .map method on the list-of-row-item scenario
export function rowTransform(table, method) {
  if (!("id" in table)) return [];
  const retList = [];
  for (let idx = 0; idx < table.id.length; ++idx) {
    retList.push(method(createRowObj(table, idx)));
  }
  return retList;
}
